import { mappingApi } from "@iventis/api/src/api";
import { onSocketMessages } from "@iventis/api/src/connection.slice";
import { SocketEvent } from "@iventis/types/loading.types";
import { useObservableCallback, useRefEffectValue } from "@iventis/utilities";
import { NotificationResourceType } from "@iventis/domain-model/model/notificationResourceType";
import { useNotificationContext } from "@iventis/notifications";
import { updateMultipleLayerMapOrderValues } from "@iventis/map-engine";
import { useDispatch, useSelector } from "react-redux";
import { getMapLayers } from "@iventis/api-helpers-mapping";
import { RootDispatch, RootState } from "./state/root.store";
import { map as mapReducer } from "./state/map.slice";
import { getMapObjectsForMap, getMapObjectAndCommentBoundsThunk } from "./state/map.slice.get-map-thunks";
import { parseLayerToIventisLayer } from "./state/map.slice.functions";
import { patchLayers } from "./services/layer-service";

/** Handles the incoming web sockets that need to update the map state. ONLY USE IN ONE PLACE */
export const useMapWebSockets = (mapId: string) => {
    const dispatch: RootDispatch = useDispatch();

    const { layers, level } = useSelector((state: RootState) => ({ layers: state.mapReducer.mapModule.layers.value, level: state.mapReducer.mapModule.currentLevel }));

    // Use ref for layers as it is used inside a observable subscription and therefore non-ref values will not be upto date
    const layersRef = useRefEffectValue(layers);

    const { createNotification } = useNotificationContext();

    useObservableCallback(onSocketMessages(SocketEvent.USER_NOTIFICATION), { signal: true }, async (value, signal) => {
        if (value.eventName === SocketEvent.USER_NOTIFICATION) {
            if (value.message.resourceType === NotificationResourceType.LayersImport || value.message.resourceType === NotificationResourceType.LayersCopy) {
                if (
                    (value.message.resourceType === NotificationResourceType.LayersImport &&
                        value.message.resourceData.mapLayersCreated &&
                        value.message.resourceData.permissionsCreated) ||
                    (value.message.resourceType === NotificationResourceType.LayersCopy && value.message.resourceData.completed)
                ) {
                    if (value.message.resourceData.mapId === mapId) {
                        let layers = await getMapLayers(mapId, mappingApi, signal);

                        const requests: Promise<unknown>[] = [];

                        // Remove all internal layers (comments, analysis etc.)
                        const previousMapLayers = layersRef.current.filter((layer) => layer.remote);

                        // When importing a layers, we want to give them a map order value
                        if (value.message.resourceType === NotificationResourceType.LayersImport) {
                            // Find the layers which are new to this map
                            const newLayers = layers
                                .filter((layer) => !previousMapLayers.some((prevLayer) => prevLayer.id === layer.id))
                                .map((layer) => parseLayerToIventisLayer(layer));

                            // Get the layers with a map order value and an array of map layer order updates
                            const { layersWithMapOrderValue, layersToUpdate } = updateMultipleLayerMapOrderValues(newLayers, previousMapLayers);

                            layers = layersWithMapOrderValue;

                            if (layersToUpdate.length > 0) {
                                // Patch with the new map order values
                                // Only need to make a network request because mapReducer.actions.replaceLayers updates the map engine with the map order values
                                requests.push(patchLayers(mapId, layersToUpdate));
                            }
                        }
                        const mapLayers = layers.map((layer) => {
                            // Find if the layer was previously in the state
                            const previousLayer = previousMapLayers.find((l) => l.id === layer.id);
                            if (previousLayer) {
                                // Get the previous layers properties such as selected, viewBy etc.
                                return { ...previousLayer, ...layer };
                            }
                            // If not in the state we add to map state specific properties
                            return parseLayerToIventisLayer(layer);
                        });
                        // Update the layers in the map
                        dispatch(mapReducer.actions.replaceLayers({ layers: mapLayers }));

                        // Get extra data for the map
                        requests.push(dispatch(getMapObjectsForMap({ mapId, pageNumber: 1, signal })));
                        requests.push(dispatch(getMapObjectAndCommentBoundsThunk({ mapId, level: `${level}`, signal })));

                        await Promise.all(requests);
                        createNotification(value.message);
                    }
                }
            }
        }
    });
};
